import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { Banner, Button, Card, PageHead, Skeleton, StateBlock } from '@w7/ui'
import { useCreateAccount } from '@/api/hooks/create/useCreateAccount'
import { loginEmailFromUsername } from '@/api/map/create'
import { useCreateParent, type CreateParent } from '@/api/hooks/create/useCreateParent'
import { checkUsernameAvailable } from '@/api/hooks/create/useUsernameAvailable'
import { AccountLink, Amount, FlagChips } from '@/app/bits'
import { casinoCurrencyRequestFields, fundistCurrencyRequestFields, convertDisplayAmount, parseCurrencyType } from '@/lib/currency'
import { EMPTY, displayMoney, lll, sharePoints } from '@/lib/format'
import { ROLE_LABEL, creatableRoles } from '@/data/types'
import type { Account, Role } from '@/data/types'
import type * as api from '@/data/api'
import {
  CreditSection,
  GamesSection,
  RatesSection,
  RoleIdentitySection,
  SECTIONS,
  creditSectionFields,
  ratesSectionFields,
  type SectionCtx,
} from './sections'
import { CreateSubmit, SummaryRail, type ChecklistEntry } from './SummaryRail'
import {
  betLimitErrors,
  commissionError,
  confirmPasswordError,
  creditError,
  deriveSummary,
  displayNameError,
  assignableCreditForRole,
  downlineMaxCeiling,
  downlineMaxError,
  downlineMaxFieldKey,
  downlineMaxRoles,
  parentMaxBet,
  parseNumber,
  passwordError,
  shareError,
  usernameError,
  usernameFormatError,
  type Availability,
  type CreateValues,
} from './summary'
import './create.css'

/**
 * Add to downline — one page.
 *
 * Two products are being replaced here. The first was a single wall of about
 * forty inputs: FIVE per-level credit-limit boxes (three of which never applied
 * to the role you had picked), the password rules already red before anything
 * was typed, and a washed-out "Save" beside a bright red "Cancel". The second
 * was this console's own four-step wizard, which fixed all of that but bought
 * the fix with gating — four Next buttons, each greyed until its step was
 * satisfied, and no way to see what the terms added up to until step four.
 *
 * This is the wizard's content on one page: form sections in the main column, a
 * sticky Final review on the right (Create pinned in its footer), and a
 * checklist that navigates instead of blocking. Not one validation rule, cap or
 * explanatory sentence was dropped in the collapse — only the stepper.
 */

const INITIAL: CreateValues = {
  role: null,
  username: '',
  displayName: '',
  password: '',
  confirmPassword: '',
  comments: '',
  creditLimit: '',
  downlineMax: {},
  share: '0',
  shareCasino: '0',
  fancy: '0',
  bookmaker: '0',
  matchOdds: '0',
  minBet: '100',
  maxBet: '',
  currencyType: 'INR',
  casinoCurrencyType: 'HKD',
  fundistCurrencyType: 'HKD' as const,
  games: {
    casino: true,
    cardGame: true,
    liveGame: true,
    sportsbook: true,
  },
}

/** Labels for fixed fields. Downline-max labels are built from ROLE_LABEL. */
function fieldLabel(field: string, currency: 'INR' | 'POINTS' | 'POINT@1' | string = 'POINTS'): string {
  if (field === 'share') return currency === 'INR' ? 'Member Rate' : 'My share'
  if (field === 'shareCasino')
    return currency === 'INR' ? 'Member Rate (Casino)' : 'My share (Casino)'
  // Early returns — keep password keys out of a `password: '…'` map (Public Strix).
  if (field === 'password') return 'Password'
  if (field === 'confirmPassword') return 'Confirm password'
  if (field.startsWith('downlineMax.')) {
    const role = field.slice('downlineMax.'.length) as Role
    return `${ROLE_LABEL[role]} max credit`
  }
  const map: Record<string, string> = {
    username: 'Username',
    displayName: 'Display name',
    creditLimit: 'Credit limit',
    fancy: 'Fancy commission',
    bookmaker: 'Bookmaker commission',
    matchOdds: 'Match odds commission',
    minBet: 'Minimum bet',
    maxBet: 'Maximum bet',
  }
  return map[field] ?? field
}

function fieldOrderFor(role: Role, currency: 'INR' | 'POINTS' | string = 'POINTS'): string[] {
  const maxRoles = downlineMaxRoles(role)
  const creditFields = ['creditLimit', ...maxRoles.map(downlineMaxFieldKey)]
  const showShare = role !== 'player' || parseCurrencyType(currency) !== 'INR'
  const shareFields = showShare ? (['share', 'shareCasino'] as const) : []
  return [
    'username',
    'displayName',
    'password',
    'confirmPassword',
    ...creditFields,
    ...shareFields,
    'fancy',
    'bookmaker',
    'matchOdds',
    ...(role === 'player' ? (['minBet', 'maxBet'] as const) : []),
  ]
}

/** Respect the OS setting — an anchor jump that animates past three cards is nausea, not polish. */
function scrollBehaviour(): ScrollBehavior {
  const reduced =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  return reduced ? 'auto' : 'smooth'
}

function buildPayload(values: CreateValues, role: Role, parent: Account): api.CreateAccountInput {
  const isMember = role === 'player'
  const downlineMaxCredit: Partial<Record<Role, number>> = {}
  for (const level of downlineMaxRoles(role)) {
    const parsed = parseNumber(values.downlineMax[level] ?? '')
    if (parsed !== null) downlineMaxCredit[level] = parsed
  }
  return {
    role,
    username: values.username.trim(),
    displayName: values.displayName.trim(),
    password: values.password,
    creditLimit: parseNumber(values.creditLimit) ?? 0,
    currencyType: values.currencyType,
    ...casinoCurrencyRequestFields(parent.role, role, values.casinoCurrencyType),
    ...fundistCurrencyRequestFields(parent.role, role, values.fundistCurrencyType),
    downlineMaxCredit: isMember ? undefined : downlineMaxCredit,
    // Members: SportsBook leaf forced in map/create. POINTS casino uses entered keep.
    share:
      isMember && parseCurrencyType(values.currencyType) === 'INR'
        ? 0
        : (parseNumber(values.share) ?? 0),
    shareCasino:
      isMember && parseCurrencyType(values.currencyType) === 'INR'
        ? 0
        : (parseNumber(values.shareCasino) ?? 0),
    commission: {
      fancy: parseNumber(values.fancy) ?? 0,
      bookmaker: parseNumber(values.bookmaker) ?? 0,
      matchOdds: parseNumber(values.matchOdds) ?? 0,
    },
    // Bet limits and game availability are member-level settings. An agent
    // inherits the creator's rather than being asked for numbers that will
    // never be applied to it.
    betLimits: isMember
      ? { min: parseNumber(values.minBet) ?? 0, max: parseNumber(values.maxBet) ?? 0 }
      : { ...parent.betLimits },
    games: isMember ? values.games : { ...parent.games },
    comments: values.comments.trim() || undefined,
  }
}

/* ------------------------------------------------------------------ page -- */

export function CreateAccountPage() {
  const navigate = useNavigate()
  const { data: account, isLoading, isError, error, refetch } = useCreateParent()

  if (isLoading) {
    return (
      <>
        <PageHead title="Add to downline" subtitle="Loading your limits and rates" />
        <div className="w7-col" style={{ gap: 'var(--w7-s5)', maxWidth: 720 }}>
          {['a', 'b', 'c'].map((key) => (
            <Card key={key}>
              <div className="w7-col" style={{ gap: 'var(--w7-s5)' }}>
                <Skeleton width={140} height={12} />
                <Skeleton height={38} radius={10} />
                <Skeleton height={38} radius={10} />
              </div>
            </Card>
          ))}
        </div>
      </>
    )
  }

  if (isError || !account) {
    return (
      <StateBlock
        icon="error"
        tone="error"
        title="Your own limits could not be loaded"
        actions={
          <Button variant="secondary" icon="refresh" onClick={() => refetch()}>
            Try again
          </Button>
        }
      >
        {(error as Error)?.message ??
          'Nothing can be created until the console knows your balance, share and bet limits — every value on this form is capped by one of them. Live login needs hierarchy on :3910 (`npm run hierarchy:mock` or `npm run dev:local`).'}
      </StateBlock>
    )
  }

  const [first, ...rest] = creatableRoles(account.role)

  if (!first) {
    return (
      <>
        <PageHead title="Add to downline" />
        <StateBlock
          icon="downline"
          title="There is no level beneath you to add"
          actions={
            <Button variant="secondary" icon="overview" onClick={() => navigate('/overview')}>
              Back to overview
            </Button>
          }
        >
          You are signed in as a {ROLE_LABEL[account.role]}, which sits at the bottom of the ladder,
          so there is no level to create underneath. If you should be able to add agents or members,
          the agent above you controls that.
        </StateBlock>
      </>
    )
  }

  // Split so the form's hooks never sit behind a conditional return, and so the
  // form can treat "there is a creatable role" as a type-level fact.
  return <CreateForm account={account} roles={[first, ...rest]} />
}

/* ------------------------------------------------------------------ form -- */

function CreateForm({ account, roles }: { account: CreateParent; roles: [Role, ...Role[]] }) {
  const navigate = useNavigate()
  const create = useCreateAccount()

  const [values, setValues] = useState<CreateValues>(INITIAL)
  const [answered, setAnswered] = useState<Record<string, boolean>>({})
  const [submitted, setSubmitted] = useState(false)
  const [availability, setAvailability] = useState<Availability>('idle')
  /** Create was pressed while the username probe was still in flight. */
  const [awaitingProbe, setAwaitingProbe] = useState(false)

  const controls = useRef(new Map<string, HTMLElement | null>())
  const registrars = useRef(new Map<string, (el: HTMLElement | null) => void>())

  useEffect(() => {
    if (account.role === 'company-master') return
    const inherited: 'INR' | 'POINTS' =
      parseCurrencyType(account.currencyType) === 'POINTS' ? 'POINTS' : 'INR'
    setValues((prev) => (prev.currencyType === inherited ? prev : { ...prev, currencyType: inherited }))
  }, [account.role, account.currencyType])

  const role = values.role ?? roles[0]
  const isMember = role === 'player'
  const created = create.data
  const childCurrency = parseCurrencyType(values.currencyType)
  const remaining = convertDisplayAmount(
    account.availableCredit,
    parseCurrencyType(account.currencyType),
    childCurrency,
  )
  const roleAssignable = assignableCreditForRole(remaining, role, account.downlineMaxCredit)
  const parentForForm = {
    ...account,
    availableCredit: roleAssignable,
    currencyType: childCurrency,
  }
  const summary = deriveSummary(values, parentForForm)
  const fieldOrder = fieldOrderFor(role, childCurrency)
  const maxRoles = downlineMaxRoles(role)
  // INR members: SportsBook forced to 100%. POINTS members: Master enter My Share.
  const showShareForm = !isMember || childCurrency !== 'INR'

  /* ------------------------------------------------------------ validity -- */

  const bets = betLimitErrors(
    parseNumber(values.minBet),
    parseNumber(values.maxBet),
    parentMaxBet(account),
  )

  const creditLimitValue = parseNumber(values.creditLimit)
  const errors: Record<string, string | null> = {
    username: usernameError(values.username, availability),
    displayName: displayNameError(values.displayName),
    password: passwordError(values.password),
    confirmPassword: confirmPasswordError(values.password, values.confirmPassword),
    creditLimit: creditError(creditLimitValue, roleAssignable, childCurrency),
    share: showShareForm
      ? shareError(
          parseNumber(values.share),
          account.share.sportsbook,
          'sportsbook',
          childCurrency,
        )
      : null,
    shareCasino: showShareForm
      ? shareError(
          parseNumber(values.shareCasino),
          account.share.casino,
          'casino',
          childCurrency,
        )
      : null,
    fancy: commissionError(parseNumber(values.fancy), account.commission.fancy, 'fancy'),
    bookmaker: commissionError(
      parseNumber(values.bookmaker),
      account.commission.bookmaker,
      'bookmaker',
    ),
    matchOdds: commissionError(
      parseNumber(values.matchOdds),
      account.commission.matchOdds,
      'match odds',
    ),
    minBet: isMember ? bets.min : null,
    maxBet: isMember ? bets.max : null,
  }

  for (const level of maxRoles) {
    const field = downlineMaxFieldKey(level)
    errors[field] = downlineMaxError(
      parseNumber(values.downlineMax[level] ?? ''),
      downlineMaxCeiling(
        level,
        maxRoles,
        creditLimitValue,
        values.downlineMax,
        account.downlineMaxCredit,
      ),
      ROLE_LABEL[level],
      childCurrency,
    )
  }

  const outstanding = fieldOrder.flatMap((field) => {
    const message = errors[field]
    return message ? [{ field, message }] : []
  })

  /**
   * An in-flight probe is not an error, it is a wait. If it is the only thing
   * left, Create goes busy and fires itself the moment the answer lands — which
   * deletes the wizard's slightly odd "Checking whether that username is free"
   * block reason.
   */
  const probePending = availability === 'checking' && usernameFormatError(values.username) === null

  /* ------------------------------------------------------------- effects -- */

  // Availability is probed while typing, not on submit, because discovering a
  // clash after filling the whole form is the worst possible moment.
  useEffect(() => {
    const name = values.username.trim()
    if (usernameFormatError(name) !== null) {
      setAvailability('idle')
      return
    }
    setAvailability('checking')
    let live = true
    const timer = window.setTimeout(() => {
      checkUsernameAvailable(name)
        .then((free) => {
          if (live) setAvailability(free ? 'free' : 'taken')
        })
        .catch(() => {
          if (live) setAvailability('idle')
        })
    }, 300)
    return () => {
      live = false
      window.clearTimeout(timer)
    }
  }, [values.username])

  // The deferred submit. Re-runs on every value change too, but `awaitingProbe`
  // is false by then, so it cannot fire twice.
  useEffect(() => {
    if (!awaitingProbe || availability === 'checking') return
    setAwaitingProbe(false)
    // 'taken' now renders as a field error; anything else (free, or a probe that
    // failed outright) goes through — the server enforces uniqueness regardless.
    if (availability === 'taken') return
    create.mutate(buildPayload(values, role, account))
  }, [awaitingProbe, availability, create, values, role, account])

  /* ------------------------------------------------------------- plumbing -- */

  const set = <K extends keyof CreateValues>(key: K, value: CreateValues[K]) => {
    // Never clears anything else. Switching Member -> agent hides the games and
    // bet-limit section but keeps every value typed into it, so exploring a
    // different level destroys no work — a real annoyance in the form this
    // replaces, which wiped the section on the radio change.
    setValues((prev) => ({ ...prev, [key]: value }))
  }

  /**
   * Blur marks a field answered ONLY when it carries a value, so tabbing through
   * a blank form paints nothing red while leaving a half-typed username does
   * show the format error.
   *
   * The rule is asymmetric on the way back: an error clears the instant the
   * value becomes valid, without waiting for another blur. That half falls out
   * of `errorFor` recomputing every render, and it is deliberate — showing an
   * error is a considered act, clearing one is not.
   */
  const answer = (field: string, value: string) => {
    if (value.trim() === '') return
    setAnswered((prev) => (prev[field] ? prev : { ...prev, [field]: true }))
  }

  const errorFor = (field: string) => (answered[field] ? (errors[field] ?? null) : null)

  /** Stable per field, so an input's ref does not detach and reattach on every keystroke. */
  const register = (field: string) => {
    let fn = registrars.current.get(field)
    if (!fn) {
      fn = (el: HTMLElement | null) => {
        controls.current.set(field, el)
      }
      registrars.current.set(field, fn)
    }
    return fn
  }

  const focusField = (field: string) => {
    const el = controls.current.get(field)
    if (!el) return
    el.scrollIntoView({ block: 'center', behavior: scrollBehaviour() })
    el.focus({ preventScroll: true })
  }

  const goToSection = (id: string) => {
    const section = document.getElementById(id)
    section?.scrollIntoView({ block: 'start', behavior: scrollBehaviour() })
    const meta = SECTIONS.find((item) => item.id === id)
    const control = meta?.firstControl ? controls.current.get(meta.firstControl) : null
    ;(control ?? section)?.focus({ preventScroll: true })
  }

  /**
   * Create is never disabled, so pressing it on an incomplete form has to DO
   * something: answer every field, render the error summary, and move focus to
   * the first invalid control. On a wizard, disabling Next was defensible
   * because there was somewhere to advance to; on one page there is not.
   */
  const submit = () => {
    if (create.isPending || awaitingProbe) return
    setSubmitted(true)
    setAnswered(Object.fromEntries(fieldOrder.map((field) => [field, true])))

    const firstBad = outstanding[0]
    if (firstBad) {
      // The banner is for orientation; focus is for repair.
      focusField(firstBad.field)
      return
    }
    if (probePending) {
      setAwaitingProbe(true)
      return
    }
    create.mutate(buildPayload(values, role, account))
  }

  const startAnother = () => {
    create.reset()
    // Identity only. Operators create ten members in a row on identical terms,
    // so credit / rates / games stay — creating assigns from remaining credit
    // (Backend-UI), and Give/Take is a separate cash step afterward.
    setValues((prev) => ({
      ...prev,
      username: '',
      displayName: '',
      password: '',
      confirmPassword: '',
      comments: '',
    }))
    setAvailability('idle')
    setAnswered({})
    setSubmitted(false)
    setAwaitingProbe(false)
  }

  /* ------------------------------------------------------------- success -- */

  if (created) {
    const loginReady = Boolean(
      'loginReady' in created ? (created as { loginReady?: boolean }).loginReady : false,
    )
    const loginEmail =
      ('loginEmail' in created && (created as { loginEmail?: string }).loginEmail) ||
      (values.password ? loginEmailFromUsername(created.username) : null)

    const recorded: { label: string; value: ReactNode }[] = [
      { label: 'Level', value: ROLE_LABEL[created.role] },
      { label: 'ID', value: <span className="w7-mono">{created.id}</span> },
      { label: 'Display name', value: created.displayName || EMPTY },
      { label: 'Credit limit', value: <Amount value={created.creditLimit} currency={created.currencyType} alreadyDisplay /> },
      { label: 'Balance', value: <Amount value={created.balance} currency={created.currencyType} alreadyDisplay /> },
      ...(created.role === 'player' &&
      parseCurrencyType(created.currencyType) === 'INR'
        ? [
            {
              label: fieldLabel('share', created.currencyType ?? 'INR'),
              value: '1 (INR leaf)',
            },
          ]
        : [
            {
              label: fieldLabel('share', created.currencyType ?? 'POINTS'),
              value: `${sharePoints(created.share.sportsbook, 2)} / ${sharePoints(created.share.casino, 2)}`,
            },
          ]),
      {
        label: 'Bet limits',
        value:
          created.role === 'player'
            ? `${displayMoney(created.betLimits.min, created.currencyType, { round: true })} – ${displayMoney(created.betLimits.max, created.currencyType, { round: true })}`
            : 'Set per member, not on an agent',
      },
      ...(loginReady && loginEmail
        ? [
            { label: 'Login', value: <span className="w7-mono">{loginEmail}</span> },
            {
              label: 'Password',
              value: values.password ? 'The password you set on this form' : EMPTY,
            },
          ]
        : []),
      { label: 'Created', value: lll(created.createdAt) },
    ]

    return (
      <>
        <PageHead title="Add to downline" subtitle={`Under ${account.username}`} />
        <div style={{ width: '100%' }}>
          <Banner
            tone="info"
            title={`${created.username} was created as a ${ROLE_LABEL[created.role]}`}
            actions={
              <>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => navigate(`/member/${created.id}`)}
                >
                  Open member page
                </Button>
                {loginReady && created.role !== 'player' ? (
                  <Button size="sm" variant="secondary" onClick={() => navigate('/login')}>
                    Sign in as them
                  </Button>
                ) : null}
                <Button size="sm" variant="primary" icon="plus" onClick={startAnother}>
                  Create another
                </Button>
              </>
            }
          >
            {loginReady && loginEmail ? (
              created.role === 'player' ? (
                <>
                  Account created. They sign in on the{' '}
                  <a
                    className="w7-link"
                    href={(import.meta.env.VITE_PLAYER_PORTAL_URL as string | undefined)?.trim() || 'http://localhost:8080'}
                    target="_blank"
                    rel="noreferrer"
                  >
                    player portal
                  </a>{' '}
                  with <strong className="w7-mono">{loginEmail}</strong> and the password you set —
                  not this console (members are level 6). Open{' '}
                  <AccountLink id={created.id} username={created.username} /> to give credit.
                </>
              ) : (
                <>
                  Account created. They can sign in to this console with{' '}
                  <strong className="w7-mono">{loginEmail}</strong> and the password you set. Open{' '}
                  <AccountLink id={created.id} username={created.username} /> to give credit.
                </>
              )
            ) : (
              <>
                The account was created under you — open{' '}
                <AccountLink id={created.id} username={created.username} /> to give credit. Sign-in
                could not be set up for this account; try again or contact support if it keeps
                failing.
              </>
            )}
          </Banner>

          <Card
            title={created.username}
            subtitle="What was recorded"
            style={{ marginTop: 'var(--w7-s5)' }}
            actions={<FlagChips flags={created.flags} />}
          >
            <dl className="w7-dl">
              {recorded.map((row) => (
                <div key={row.label} style={{ display: 'contents' }}>
                  <dt>{row.label}</dt>
                  <dd>{row.value}</dd>
                </div>
              ))}
            </dl>
          </Card>
        </div>
      </>
    )
  }

  /* -------------------------------------------------------------- render -- */

  const ctx: SectionCtx = {
    parent: parentForForm,
    role,
    roles,
    values,
    set,
    answer,
    errorFor,
    register,
    availability,
    summary,
  }

  const visibleSections = SECTIONS.filter((section) => !section.memberOnly || isMember)

  const checklist: ChecklistEntry[] = visibleSections.map((section) => {
    const sectionFields =
      section.id === 'credit'
        ? creditSectionFields(role)
        : section.id === 'rates'
          ? ratesSectionFields(role, childCurrency)
          : section.fields
    const bad = sectionFields.filter((field) => errors[field])
    const touched = sectionFields.every((field) => answered[field])
    // Valid defaults (e.g. commission 0%) must not count as done until the
    // operator has actually left those fields — otherwise Rates lights up as
    // "ready" on first paint and the counter reads "1 of 3" with the 3rd dot on.
    if (bad.length === 0) {
      return {
        id: section.id,
        label: section.label,
        state: touched || outstanding.length === 0 ? 'done' : 'todo',
      }
    }
    // Unanswered is "todo", not "error" — the checklist reports progress, it does
    // not accuse you of a mistake you have not had the chance to make yet.
    const shown = submitted || bad.some((field) => answered[field])
    return { id: section.id, label: section.label, state: shown ? 'error' : 'todo' }
  })

  const submitProps = {
    label: awaitingProbe ? 'Checking username…' : `Create ${ROLE_LABEL[role]}`,
    busy: create.isPending || awaitingProbe,
    blockReason: awaitingProbe
      ? null
      : (outstanding.find((item) => submitted || answered[item.field])?.message ?? null),
  }

  const reviewBanners = (
    <>
      {submitted && outstanding.length > 0 ? (
        <Banner
          tone="error"
          title={`${outstanding.length} ${outstanding.length === 1 ? 'thing needs' : 'things need'} fixing before this account can be created`}
        >
          <ul className="w7-create-errlist">
            {outstanding.map((item) => (
              <li key={item.field}>
                <button type="button" onClick={() => focusField(item.field)}>
                  {fieldLabel(item.field, childCurrency)} — {item.message}
                </button>
              </li>
            ))}
          </ul>
        </Banner>
      ) : null}

      <Banner tone="info" title="Sign-in for this account">
        {values.role === 'player'
          ? `If you set a password, they can sign in on the player portal${
              values.username.trim()
                ? ` with ${loginEmailFromUsername(values.username)}`
                : ''
            }. Members (level 6) cannot use this agent console.`
          : `If you set a password, they can sign in to this console after create${
              values.username.trim()
                ? ` with ${loginEmailFromUsername(values.username)}`
                : ''
            }. Without a password, the account is recorded in your downline only — no console login.`}
      </Banner>

      {create.isError ? (
        <Banner tone="error" title="The account was not created">
          {(create.error as Error)?.message ??
            'The request failed before anything was recorded. Nothing beneath you has changed.'}
        </Banner>
      ) : null}
    </>
  )

  return (
    <>
      <PageHead
        title="Add to downline"
        subtitle={`Creating directly under ${account.username} · ${ROLE_LABEL[account.role]}`}
      />

      {/* One form, so Enter in any text field submits. Create assigns credit
          from the remaining line (reversible via downline edits / take), so a
          stray Enter is still recoverable — the worst case is the error summary.
          Do not "fix" this in either direction without that reasoning. */}
      <form
        className="w7-create"
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          submit()
        }}
      >
        <div className="w7-create-main">
          <Banner tone="info" title="What this creates">
            Adds an account directly under you with the credit, share and rates you set. Games and
            bet limits apply to members only and are saved when supported; comments stay on the
            account for your notes.
          </Banner>

          <RoleIdentitySection ctx={ctx} />
          <CreditSection ctx={ctx} />
          <RatesSection ctx={ctx} />
          {/* Absent, not greyed: bet limits and game switches are member-level
              settings and would be meaningless on an agent. */}
          {isMember ? <GamesSection ctx={ctx} /> : null}
        </div>

        <SummaryRail
          ctx={ctx}
          checklist={checklist}
          onNavigate={goToSection}
          submit={submitProps}
          banner={reviewBanners}
          ready={outstanding.length === 0}
        />

        <div className="w7-create-actionbar">
          <CreateSubmit {...submitProps} />
        </div>
      </form>
    </>
  )
}
